import type { ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { getImageAttrs } from '~/modules/images';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ACTION_CARD_WEBSITE_IMAGE from './images/action-card-website.webp';
import ACTION_CARD_CONTACT_IMAGE from './images/action-card-contact.webp';

/**
 * The two-card invitation the detail pages close their offer with: a
 * photograph each, a sentence saying what the reader gets, and the action at
 * the foot — the whole card the control. Built first for the product page's
 * "Get more Specific" pair; the project's hire-this-practice pair draws the
 * same cards with its own copy and pictures.
 */

/** Bundled frames for a card whose page has no photograph to give it. */
export { ACTION_CARD_WEBSITE_IMAGE, ACTION_CARD_CONTACT_IMAGE };

const BUNDLED_FRAMES = [ACTION_CARD_WEBSITE_IMAGE, ACTION_CARD_CONTACT_IMAGE];

/** The card's photograph, filling a tile around 900px wide on the desktop grid. */
const CARD_SIZES = getImagesSizes({ desktop: 900, mobile: 414 });

/** The peek card's thumbnail: 36% of the card, square. */
const PEEK_WIDTH_PX = 340;

/**
 * A card picture's `<img>` attributes.
 *
 * The page's own photography goes through the image service at the size the
 * card draws it — the pages hand these cards a project banner, which is
 * several megabytes at its authored size and was being pulled whole for a
 * tile. The bundled frames are left alone: they are build assets, and the
 * service has no record to make a rendition from.
 */
const actionCardImageAttrs = (
    image: string,
    options: { sizes?: string; width?: number },
) =>
    BUNDLED_FRAMES.includes(image)
        ? { src: image }
        : getImageAttrs(image, options);

/**
 * The section's heading, as "More products from …" sets it (SectionHead):
 * 72px, medium, leading-none, -0.032em. Ink on the light page; the dark
 * project page passes its own colour.
 */
export const ACTION_CARDS_HEADING_CLASS =
    'm-0 font-sans leading-none font-medium max-md:text-[2rem] max-md:tracking-[-0.03em] md:text-[4.5rem] md:tracking-[-0.032em]';

/** The pair's grid, 50px under the heading. */
export const ACTION_CARDS_GRID_CLASS =
    'grid max-md:mt-12 max-md:grid-cols-1 max-md:gap-4 md:mt-[3.125rem] md:grid-cols-2 md:gap-[2.125rem]';

/**
 * The card: a photograph with the invitation over it and the action at its
 * foot. Sized to the Figma's 770x505 as a ratio rather than a height, so the
 * pair keeps its proportions at any width.
 *
 * The whole card is the control — an anchor on one, a button on the other —
 * so the target is the photograph rather than a pill in the corner of it.
 */
export const ACTION_CARD_CLASS = cn(
    'group relative flex aspect-[770/505] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-tile',
    // `items-start` is load-bearing: one of these cards is a TrackedWebsiteCta,
    // which merges the pill recipe's own `items-center` in under whatever it is
    // given. Left to that, the card centred its copy and its action while the
    // plain button beside it left-aligned both.
    'items-start text-left no-underline max-md:p-6 md:p-[2rem]',
);

/**
 * The invitation. White on a photograph, so the card carries a scrim of its
 * own rather than relying on whichever image it happens to hold.
 */
const COPY_CLASS =
    'relative m-0 font-sans font-medium text-white max-md:text-base max-md:leading-[1.35] md:max-w-[40rem] md:text-[1.125rem] md:leading-[1.35]';

/** Reaches only as far down as the copy does; the action carries its own. */
const SCRIM_CLASS =
    'bg-linear-to-b absolute inset-x-0 top-0 h-[45%] from-black/45 to-transparent';

/**
 * The bare host of a website — "ecc.co.nz", not the tracking URL the card
 * actually carries. A stored value may have no scheme, which `URL` will not
 * parse, so one is assumed.
 */
export const hostOf = (website: string): string => {
    try {
        return new URL(
            /^https?:\/\//i.test(website) ? website : `https://${website}`,
        ).hostname.replace(/^www\./i, '');
    } catch {
        return '';
    }
};

/**
 * A browser window that rises out of the card on hover.
 *
 * Not a screenshot of the site: nothing in the data model holds one, and a
 * third-party shot service would put every supplier's URL through an external
 * host to fetch an image on page load. This says the same thing — that the
 * card leads off ArchiPro to their own site — with the practice's name and
 * their domain, and it is ours.
 *
 * Desktop only, and inert: there is no hover to open it on touch, and the card
 * underneath is the control.
 */
export const WebsitePeek = ({
    supplier,
    host,
}: {
    supplier: string;
    host: string;
}) => (
    <div className="pointer-events-none absolute inset-0 grid place-items-center max-md:hidden">
        <div
            className={cn(
                'aspect-[770/505] w-[44%] overflow-hidden rounded-[0.75rem] bg-white',
                'shadow-[0_1.5rem_3rem_rgba(0,0,0,0.28)]',
                // Travel only, no fade: the card clips it, so it rises out of
                // the bottom edge and drops back through it on the way out.
                // Fading as well made it read as appearing rather than
                // arriving.
                // 175%, not 100%: the frame is centred in the card, so its
                // travel has to clear half the card as well as its own height
                // or it sits peeking over the bottom edge at rest.
                'translate-y-[175%] transition-transform duration-500',
                '[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
                'group-hover:translate-y-0',
                'motion-reduce:transition-none',
            )}
        >
            {/* The chrome, at its most minimal: three dots and a bar standing
                in for the address field. */}
            <div className="flex items-center gap-[0.75rem] border-b border-black/[0.08] px-[1rem] py-[0.75rem]">
                <span className="flex shrink-0 gap-[0.3125rem]">
                    <span className="size-[0.5rem] rounded-full bg-black/15" />
                    <span className="size-[0.5rem] rounded-full bg-black/15" />
                    <span className="size-[0.5rem] rounded-full bg-black/15" />
                </span>
                <span className="h-[1.125rem] flex-1 rounded-full bg-black/[0.05]" />
            </div>

            <div className="flex h-full flex-col items-center justify-center gap-[0.5rem] pb-[3rem]">
                <p className="m-0 px-[1.5rem] text-center font-gestura text-[2rem] leading-[1.1] font-normal text-[#181818]">
                    {supplier}
                </p>
                {host ? (
                    <p className="m-0 flex items-center gap-[0.25rem] font-sans text-[0.9375rem] leading-none text-[#767676]">
                        {host}
                        <ArrowUpRight
                            aria-hidden="true"
                            className="size-[1rem]"
                        />
                    </p>
                ) : null}
            </div>
        </div>
    </div>
);

/**
 * The enquiry card's counterpart to the website window: the item itself on a
 * white card, under its own name — the product for the supplier, the project
 * for the practice.
 */
export const ContactPeek = ({
    image,
    title,
    subtitle,
}: {
    image?: string;
    title: string;
    subtitle: string;
}) => (
    <div className="pointer-events-none absolute inset-0 grid place-items-center max-md:hidden">
        <div
            className={cn(
                'w-[36%] rounded-[0.75rem] bg-white',
                'shadow-[0_1.5rem_3rem_rgba(0,0,0,0.28)]',
                // Same travel as the window on the other card, so the pair
                // behaves as one idea.
                'translate-y-[175%] transition-transform duration-500',
                '[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
                'group-hover:translate-y-0',
                'motion-reduce:transition-none',
            )}
        >
            <div className="relative">
                {image ? (
                    <img
                        {...actionCardImageAttrs(image, {
                            width: PEEK_WIDTH_PX,
                        })}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="aspect-square w-full rounded-t-[0.75rem] object-cover"
                    />
                ) : (
                    <div className="bg-neutral-100 aspect-square w-full rounded-t-[0.75rem]" />
                )}
            </div>

            {/* The tile system's own caption: 14px, the title in ink and the
                maker a step back from it. */}
            <div className="flex flex-col gap-[0.125rem] px-[0.875rem] pt-[0.75rem] pb-[1rem]">
                <p className="m-0 truncate font-sans text-[0.875rem] leading-[1.3] font-medium text-[#323232]">
                    {title}
                </p>
                <p className="m-0 truncate font-sans text-[0.875rem] leading-[1.3] text-[#767676]">
                    {subtitle}
                </p>
            </div>
        </div>
    </div>
);

/**
 * The action, drawn as a button but not one: the card around it takes the
 * click, and a real control here would be both a nested interactive element
 * and a second tab stop for the same thing.
 */
export const ACTION_CARD_ACTION_CLASS = 'pointer-events-none relative w-fit';

export const ActionCardBody = ({
    image,
    copy,
    action,
    peek,
    hoverTint = false,
}: {
    image: string;
    copy: ReactNode;
    action: ReactNode;
    peek?: ReactNode;
    /**
     * Darkens the photograph on hover, under everything else on the card. The
     * enquiry card grows a white product card over its picture, and a busy
     * photograph left that card competing with what was behind it.
     */
    hoverTint?: boolean;
}) => (
    <>
        <img
            {...actionCardImageAttrs(image, { sizes: CARD_SIZES })}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
        />
        <div className={SCRIM_CLASS} />
        {/* Below md, a flat 10% black over the whole photograph: the scrim
            only darkens the top, and a white studio shot left the white copy
            with nothing to stand on. Desktop keeps the picture clean. */}
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-black/10 md:hidden"
        />
        {/* Over the photograph and under the rest: the peek card, the copy and
            the action all come after it, so they keep their own contrast. */}
        {hoverTint ? (
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100 motion-reduce:transition-none"
            />
        ) : null}
        {peek}
        <p className={COPY_CLASS}>{copy}</p>
        {action}
    </>
);
